import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, Spin } from 'antd';
import CustomInput from '../../common/inputs/CustomInput';
import { loginAction, loginSsoAction } from '../../../redux/actions/authActions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { resetSpin } from '../../../redux/reducers/authReducer/AuthSlice';
import { PrimaryButton } from '../../../styles/common.styles';
import { getProjectInformationAction } from '../../../redux/actions/projectActions';
import {
    setAcceptedInvitation,
    setProjectPackageId,
} from '../../../redux/reducers/projectsReducer/ProjectSlice';
import { setFilePath, setPackageId } from '../../../redux/reducers/productReducer/ProductSlice';
import { SubscriptionTypes } from '../../../models/subscription/enums';
import {
    ButtonEmptySpace,
    ButtonFormItem,
    ForgotPasswordLink,
    LoginButtonContainer,
    LoginDivider,
    LoginFormStyle,
    LoginInputs,
    LoginInputsContainer,
    LoginSubTitle,
    LoginTitle,
    SsoButton,
    SsoContainer,
    SsoSubtitle,
} from './LoginForm.styles';
import {
    LoginActionResponse,
    LoginSsoActionResponse,
} from '../../../redux/actions/authActions/types';
import { uploadDocumentByName } from '../../../helpers/DOM';
import { setAccountTypeCookie, setCookies } from '../../../redux/persistedState';
import { getAccountRole } from '../../../helpers/extract';
import { alertService } from '../../../services';
import { AlertMessages } from '../../alert/AlertMessages';

const LoginForm: React.FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const dispatch = useAppDispatch();
    const selector = useAppSelector((state) => state);
    const { authReducer, projectReducer, productReducer } = selector;

    const history = useHistory();

    useEffect(() => {
        dispatch(resetSpin());
    }, []);

    const [state, setState] = useState<State>({
        userName: '',
    });

    const [ssoUserName, setSsoUserName] = useState<string>('');

    const codeUrl = params.get('code');
    const stateUrl = params.get('state');
    const errorMessage = params.get('errorMessage');

    const ssoLoginParam = params.get('sso-login');
    const isSsoLogin = ssoLoginParam ? JSON.parse(ssoLoginParam) : false;


    useEffect(() => {
        if (codeUrl && codeUrl !== '') {
            document.location.href = `./sso-auth?code=${codeUrl}&state=${stateUrl}`;
        }
    }, [codeUrl]);

    useEffect(() => {
        if (errorMessage && errorMessage !== '') {
            alertService.error(errorMessage, {autoClose: false});
        }
    }, [errorMessage])

    const handleUserNameChange = (event) => {
        const { value } = event.target;
        console.log(state.userName);
        setState({ ...state, userName: value });
    };

    const handleSsoUserNameChange = (event) => {
        const { value } = event.target;
        setSsoUserName(value);
    };

    const onSubmit = async (data: { userName: string; password: string }) => {
        if (!data.userName.trim()) {
            return;
        }
        const { payload } = (await dispatch(
            loginAction({ ...data, userName: data.userName.trim() }),
        )) as LoginActionResponse;
        if (payload?.user) {
            setCookies(payload.tokens.accessToken, payload.tokens.refreshToken);
            const accRole = getAccountRole(payload.user.accountUser, payload.user.parentAccountId).accountRole
            setAccountTypeCookie(accRole);
            const userRole = payload.user?.userRoles[0].role?.name;
            const returnUrl = params.get('return');
            if (returnUrl) {
                window.location.href = returnUrl;
            }
            if (payload.user.subscription.planName !== SubscriptionTypes.FREE) {
                if (productReducer.packageId) {
                    history.push(`/project-library/${productReducer.packageId}`);
                    dispatch(setPackageId(undefined));
                    return;
                }
                if (projectReducer.packageIdData && userRole !== 'SMAdmin') {
                    history.push(
                        `/project-info/${projectReducer.packageIdData?.projectId}/${projectReducer.packageIdData?.packageId}/2`,
                    );
                    dispatch(setProjectPackageId(undefined));
                    return;
                }
                if (productReducer.filePath) {
                    uploadDocumentByName(productReducer.filePath);
                    dispatch(setFilePath(undefined));
                }
            }
            if (projectReducer.acceptedInvitation) {
                await dispatch(
                    getProjectInformationAction(projectReducer.acceptedInvitation.toString()),
                );
                dispatch(setAcceptedInvitation(0));
            }
            if (userRole !== 'SMAdmin') {
                history.push('/dashboard');
            } else {
                history.push('/users');
            }
        }
    };

    const onSubmitSso = async () => {
        const { payload } = (await dispatch(
            loginSsoAction({ userName: ssoUserName.trim() }),
        )) as LoginSsoActionResponse;

        if (payload.data.url) {
            document.location.href = payload.data.url;
        } else {
            alertService.error(AlertMessages.SUBSCRIPTION_NOT_FOUND, {autoClose: false});
        }
    };

    const handleSsoClick = () => {
        history.push('/login?sso-login=true');
    };

    return (
        <>
            {!isSsoLogin ? (
                <LoginFormStyle
                    name="basic"
                    labelCol={{ span: 6 }}
                    initialValues={{ remember: false }}
                    onFinish={onSubmit}
                    className="login-form-container"
                    autoComplete="off"
                >
                    <LoginTitle>Welcome! Log in.</LoginTitle>
                    <LoginSubTitle>
                        We’re thrilled to have your organization evaluate Transparency Catalog
                        Project Builder & Library. Log in with the credentials we sent you.
                    </LoginSubTitle>
                    <LoginInputsContainer>
                        <LoginInputs>
                            <CustomInput
                                Component={Input}
                                label="Email"
                                name="userName"
                                props={{ onChange: handleUserNameChange }}
                                rules={[{ required: true, message: '' }]}
                                forbidWhitespace={true}
                            />

                            <CustomInput
                                Component={Input.Password}
                                label="Password"
                                name="password"
                                rules={[{ required: true, message: '' }]}
                            />

                            <ButtonFormItem>
                                <ButtonEmptySpace></ButtonEmptySpace>
                                <LoginButtonContainer>
                                    <PrimaryButton htmlType="submit">
                                        {!authReducer.pending ? <>Log in</> : <Spin size="small" />}
                                    </PrimaryButton>
                                    <ForgotPasswordLink to="/forgot-password">
                                        Forgot password?
                                    </ForgotPasswordLink>
                                </LoginButtonContainer>
                            </ButtonFormItem>
                        </LoginInputs>

                        <div>
                            <LoginDivider type="vertical" />
                        </div>

                        <SsoContainer>
                            <SsoSubtitle>Organization uses single sign-on?</SsoSubtitle>

                            <SsoButton onClick={handleSsoClick}>Login with SSO</SsoButton>
                        </SsoContainer>
                    </LoginInputsContainer>
                </LoginFormStyle>
            ) : (
                <LoginFormStyle
                    name="sso"
                    labelCol={{ span: 6 }}
                    initialValues={{ remember: false }}
                    onFinish={onSubmitSso}
                    className="login-form-container"
                    autoComplete="off"
                >
                    <LoginTitle>Log in with single sign-on</LoginTitle>
                    <LoginInputs>
                        <CustomInput
                            Component={Input}
                            label="Email"
                            name="ssoUserName"
                            props={{ onChange: handleSsoUserNameChange, value: ssoUserName }}
                            rules={[{ required: true, message: '' }]}
                            forbidWhitespace={true}
                        />

                        <ButtonFormItem>
                            <ButtonEmptySpace></ButtonEmptySpace>
                            <LoginButtonContainer>
                                <PrimaryButton htmlType="submit">
                                    {!authReducer.pending ? <>Continue</> : <Spin size="small" />}
                                </PrimaryButton>
                            </LoginButtonContainer>
                        </ButtonFormItem>
                    </LoginInputs>
                </LoginFormStyle>
            )}
        </>
    );
};

export default LoginForm;
